import { ArrowDownRight, Timer, Gamepad2, ShieldCheck } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1678558563173-e4c279c4ea82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxtaWxpdGFyeSUyMGRhcmslMjBvcmFuZ2UlMjBzdW5zZXR8ZW58MHx8fHwxNzgzODc2NDE2fDA&ixlib=rb-4.1.0&q=85";

export default function Hero({ fromPrice = 50, deliveryMinutes = 5 }) {
  return (
    <header id="top" className="relative isolate overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-black via-black/85 to-black/40" />
      <div className="bo2-scanlines absolute inset-0" />
      <div
        className="pointer-events-none absolute -left-24 top-1/3 h-40 w-[560px] rotate-[-6deg] bo2-tape opacity-20"
        aria-hidden
      />

      <div className="relative z-10 mx-auto flex max-w-7xl flex-col gap-14 px-6 pb-32 pt-24 md:px-12 md:pt-32 lg:px-20 lg:pt-40">
        <div className="flex items-center gap-3 font-mono-hud text-xs uppercase tracking-widest text-[#ff6b00]">
          <span className="inline-block h-2 w-2 bg-[#ff6b00] bo2-pulse" />
          <span data-testid="hero-status">SYSTEM ONLINE // PS4 TESTKIT ARMED</span>
        </div>

        <div className="max-w-4xl">
          <p className="font-mono-hud text-sm uppercase tracking-[0.4em] text-zinc-400">
            Call of Duty · Black Ops II · Modding Ops
          </p>
          <h1
            data-testid="hero-title"
            className="mt-4 font-display text-6xl leading-[0.9] tracking-tight text-zinc-100 sm:text-7xl lg:text-[8rem]"
          >
            LEVEL UP.<br />
            <span className="text-[#ff6b00] bo2-text-glow">UNLOCK ALL.</span>
          </h1>
          <p className="mt-6 max-w-2xl font-hud text-lg text-zinc-300 sm:text-xl">
            Full account level service + unlock all for
            <span className="mx-2 border border-[#ff6b00] px-2 py-0.5 font-mono-hud text-sm uppercase tracking-widest text-[#ff6b00]">
              Black Ops 2
            </span>
            delivered directly through a PS4 Testkit. No bans. No BS.
          </p>
          <p className="mt-4 max-w-2xl font-mono-hud text-xs uppercase tracking-widest text-zinc-500">
            <span className="text-[#ff6b00]">⚠</span> ~5 min to complete once your order is picked up. Queue may be slow during high volume.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <a
            href="#service"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-3 border-2 border-[#ff6b00] bg-[#ff6b00] px-8 py-4 font-display text-lg uppercase tracking-widest text-black transition-transform duration-150 hover:-translate-y-1 hover:bg-[#ff8125] bo2-pulse"
          >
            View Packages — from ${fromPrice}
            <ArrowDownRight className="h-5 w-5 transition-transform duration-150 group-hover:translate-x-1 group-hover:translate-y-1" />
          </a>
          <a
            href="#how"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 border-2 border-zinc-700 bg-black/40 px-6 py-4 font-hud text-sm uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:border-[#ff6b00] hover:text-[#ff6b00]"
          >
            View Mission Brief
          </a>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-3">
          <Stat
            icon={<Timer className="h-5 w-5 text-[#ff6b00]" />}
            label="Turnaround"
            value={`~${deliveryMinutes} MIN`}
            hint="once picked up"
            testId="stat-delivery"
          />
          <Stat
            icon={<Gamepad2 className="h-5 w-5 text-[#ff6b00]" />}
            label="Platform"
            value="PS4 / PS5 · BO2"
            testId="stat-platform"
          />
          <Stat
            icon={<ShieldCheck className="h-5 w-5 text-[#ff6b00]" />}
            label="Method"
            value="PS4 Testkit"
            testId="stat-method"
          />
        </div>
      </div>
    </header>
  );
}

function Stat({ icon, label, value, hint, testId }) {
  return (
    <div className="flex items-center gap-4 bg-black px-6 py-6" data-testid={testId}>
      <div className="grid h-10 w-10 place-items-center border border-[#ff6b00]/40 bg-zinc-950">
        {icon}
      </div>
      <div>
        <div className="font-mono-hud text-xs uppercase tracking-widest text-zinc-500">
          {label}
        </div>
        <div className="font-display text-xl tracking-wide text-zinc-100">{value}</div>
        {hint ? (
          <div className="mt-0.5 font-mono-hud text-[10px] uppercase tracking-widest text-zinc-600">
            {hint}
          </div>
        ) : null}
      </div>
    </div>
  );
}
